/* IntelApps — Project Detail Page */
import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header, { Arrow } from '../parts/Header.js';
import Footer from '../parts/Footer.js';
import DiscussCTA from '../parts/Discuss.js';

const PROJECTS = [
  {
    id: 'DoorDash',
    title: 'DoorDash — Food Delivery App',
    subtitle: 'Food Delivery App',
    meta: 'Mobile · iOS + Android',
    img: '/assets/portfolio/doordash.png',
    tag: 'Mobile',
    result: '↑ 4.7★ App Store',
    timeline: '12 weeks',
    stack: ['React Native', 'Firebase', 'WebRTC', 'Node.js'],
    description: 'Delivery anywhere you are. DoorDash offers the greatest online selection of your favorite restaurants and stores, facilitating delivery of freshly prepared meals, groceries, OTC medicines, flowers & more.',
  },
  {
    id: 'ankerkrut',
    title: 'Shopify - Kitchen & Bath Appliances',
    subtitle: 'Kitchen & Bath Appliances',
    meta: 'Web · Kitchen & Bath Appliances',
    img: '/assets/portfolio/ankerkrut.png',
    tag: 'Web',
    result: '120K+ products',
    timeline: '14 weeks',
    stack: ['Next.js', 'Postgres', 'Prisma', 'Vercel'],
    description: 'Ankerkrut is a leading kitchen and bath appliance store, offering a wide range of products for your home.',
  },
  {
    id: 'oanda',
    title: 'Oanda - Forex Trading Platform',
    subtitle: 'Forex Trading Platform',
    meta: 'Web · Forex Trading',
    img: '/assets/portfolio/oanda.png',
    tag: 'Mobile',
    result: '100% uptime',
    timeline: '10 weeks',
    stack: ['React', 'Node.js', 'Express', 'MongoDB'],
    description: 'Oanda is a leading forex trading platform, offering a wide range of products for your trading needs.',
  },
  {
    id: 'staymenity',
    title: 'Staymenity',
    subtitle: 'SaaS platform for short-term rental property management.',
    meta: 'SaaS · Property tech',
    img: '/assets/portfolio/amenity.png',
    tag: 'Web',
    result: '40% cost reduction',
    timeline: '16 weeks',
    stack: ['Next.js', 'Postgres', 'Prisma', 'Vercel'],
    description: 'Developed a comprehensive property management platform with booking, payments, and automation features.',
  },
  {
    id: 'winify',
    title: 'Winify AI Agent',
    subtitle: 'AI-powered RFP automation platform.',
    meta: 'AI · RFP automation',
    img: '/assets/portfolio/aiagent.png',
    tag: 'AI',
    result: '96% time saved',
    timeline: '14 weeks',
    stack: ['LangGraph', 'OpenAI', 'Next.js', 'Postgres'],
    description: 'Built an AI agent that automates RFP responses, cutting turnaround time from 11 days to 6 hours.',
  },
  {
    id: 'noble',
    title: 'Nobleblocks',
    subtitle: 'Scientific publishing platform on blockchain.',
    meta: 'DApp · Scientific publishing',
    img: '/assets/portfolio/scifi.png',
    tag: 'Blockchain',
    result: '$2.1M settled week one',
    timeline: '18 weeks',
    stack: ['Solidity', 'Foundry', 'Next.js', 'IPFS'],
    description: 'Developed a decentralized scientific publishing platform with peer review and tokenized incentives.',
  },
];

export function ProjectDetailPage() {
  const { id } = useParams();
  const p = PROJECTS.find((x) => x.id === id) || PROJECTS[0];

  useEffect(() => {
    document.title = `IntelApps — ${p.title}`;
  }, [p]);

  return (
    <>
      <Header />
      <main>
        <section className="page-hero" style={{ paddingBottom: '32px' }}>
          <div className="container">
            <Link
              to="/work"
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '12px',
                color: 'var(--ink-3)',
                textDecoration: 'none',
              }}
            >
              ← Back to work
            </Link>
            <div style={{ marginTop: '24px' }}>
              <span className="eyebrow">Case study</span>
              <h1 style={{ marginTop: '12px' }}>{p.title}</h1>
              <p className="page-hero-sub">{p.subtitle}</p>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '32px',
                marginTop: '32px',
                paddingTop: '32px',
                borderTop: '1px solid var(--line)',
              }}
            >
              <div>
                <p
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: '11px',
                    color: 'var(--ink-4)',
                    textTransform: 'uppercase',
                    marginBottom: '8px',
                  }}
                >
                  Outcome
                </p>
                <p style={{ fontSize: '24px', fontWeight: 700, color: 'var(--emerald)' }}>{p.result}</p>
              </div>
              <div>
                <p
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: '11px',
                    color: 'var(--ink-4)',
                    textTransform: 'uppercase',
                    marginBottom: '8px',
                  }}
                >
                  Timeline
                </p>
                <p style={{ fontSize: '24px', fontWeight: 700 }}>{p.timeline}</p>
              </div>
              <div>
                <p
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: '11px',
                    color: 'var(--ink-4)',
                    textTransform: 'uppercase',
                    marginBottom: '8px',
                  }}
                >
                  Stack
                </p>
                <p style={{ fontSize: '16px', fontWeight: 600 }}>{p.stack.join(', ')}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="section" style={{ paddingTop: '32px' }}>
          <div className="container">
            <div
              style={{
                borderRadius: '24px',
                overflow: 'hidden',
                border: '1px solid var(--line)',
                marginBottom: '48px',
              }}
            >
              <img src={p.img} alt={p.title} style={{ width: '100%', height: 'auto', display: 'block' }} />
            </div>

            <div style={{ maxWidth: '720px' }}>
              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: 700,
                  marginBottom: '16px',
                }}
              >
                About the project
              </h2>
              <p
                style={{
                  fontSize: '17px',
                  lineHeight: 1.6,
                  color: 'var(--ink-2)',
                  marginBottom: '32px',
                }}
              >
                {p.description}
              </p>

              <h3
                style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  marginBottom: '12px',
                }}
              >
                The challenge
              </h3>
              <p
                style={{
                  fontSize: '16px',
                  lineHeight: 1.6,
                  color: 'var(--ink-3)',
                  marginBottom: '24px',
                }}
              >
                The client needed a{' '}
                {p.tag.toLowerCase()}
                {' '}
                solution that could scale to millions of users while maintaining performance
                {' '}
                and reliability.
                {' '}
                They had tried multiple vendors but couldn&apos;t find a team
                {' '}
                that could deliver on time.
              </p>

              <h3
                style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  marginBottom: '12px',
                }}
              >
                The solution
              </h3>
              <p
                style={{
                  fontSize: '16px',
                  lineHeight: 1.6,
                  color: 'var(--ink-3)',
                  marginBottom: '24px',
                }}
              >
                We assembled a cross-functional team and delivered in {p.timeline}. Using{' '}
                {p.stack.slice(0, 2).join(' and ')}, we built a production-ready platform that exceeded all performance
                metrics.
              </p>

              <div
                style={{
                  background: 'var(--bg-soft)',
                  borderRadius: '20px',
                  padding: '32px',
                  marginTop: '48px',
                }}
              >
                <h3 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '16px' }}>Want results like this?</h3>
                <p style={{ fontSize: '15px', color: 'var(--ink-3)', marginBottom: '20px' }}>
                  Book a free 30-minute scoping call. We&apos;ll tell you if we&apos;re a fit.
                </p>
                <a href="/discuss-project" className="btn btn-accent btn-arrow">
                  Start a project <Arrow size={14} />
                </a>
              </div>
            </div>
          </div>
        </section>
        <DiscussCTA />
      </main>
      <Footer />
    </>
  );
}

export default ProjectDetailPage;
